import moment from "moment"
import { DATE_KEY_FORMAT, LISTING_TYPES, REASON_TYPES, SLOT_DISPLAY_FORMAT, SLOT_VALUE_FORMAT, VISIBLE_SLOT_COUNT } from "./constants"

/**
 * The visible month grid: whole weeks, Sunday to Saturday, so the first and last rows spill into
 * the neighbouring months. Same window the request asks for, which is why the endpoint takes the
 * grid's bounds rather than the month's.
 */
export const buildMonthGrid = (monthDate) => {
  const start = moment(monthDate).startOf("month").day(0)
  const end = moment(monthDate).endOf("month").day(6)

  const weeks = []
  const cursor = start.clone()
  while (cursor.isSameOrBefore(end, "day")) {
    const week = []
    for (let index = 0; index < 7; index += 1) {
      week.push({ dateKey: cursor.format(DATE_KEY_FORMAT), isCurrentMonth: cursor.isSame(monthDate, "month") })
      cursor.add(1, "day")
    }
    weeks.push(week)
  }
  return { weeks, start: start.format(DATE_KEY_FORMAT), end: end.format(DATE_KEY_FORMAT) }
}

/** "10:00" -> "10:00 AM". */
export const formatSlotTime = (time) => moment(time, SLOT_VALUE_FORMAT).format(SLOT_DISPLAY_FORMAT)

/**
 * The collapsed slot line: the first few times joined, plus how many are left over.
 *
 * `slots_total` is preferred over `slots.length` because the two differ — `slots` is what is still
 * bookable while the total counts the day's whole schedule, and the design's "+N more" counts the
 * remaining bookable ones. Falls back to the array when the field is absent.
 */
export const summariseSlots = (day, visibleCount = VISIBLE_SLOT_COUNT) => {
  const slots = day?.slots || []
  const visible = slots.slice(0, visibleCount)
  return {
    times: visible.map((slot) => formatSlotTime(slot.time)),
    remainingCount: Math.max(slots.length - visible.length, 0)
  }
}

/** "09:00 AM · 1 spot left" — ticketed listings state capacity per slot (§3). */
export const formatTicketedSlot = (slot, t) => {
  const time = formatSlotTime(slot.time)
  if (slot.spots_left === 0) {
    return t("availability.slot_fully_booked", {
      defaultValue: "{{time}} · Fully Booked ({{booked}}/{{capacity}})",
      time,
      booked: slot.booked ?? slot.capacity,
      capacity: slot.capacity
    })
  }
  return t("availability.slot_spots_left", { defaultValue: "{{time}} · {{count}} spot left", time, count: slot.spots_left })
}

export const isTicketedListing = (listing) => listing?.listing_type === LISTING_TYPES.TICKETED

/**
 * True when a reason renders as a solid scope pill rather than the white conflict chip.
 *
 * Blackouts and crew-off are statements the guide made; the shared-resource reasons are
 * consequences of scheduling. The design gives the two groups different chips.
 */
export const isBlackoutReason = (reason) => reason?.type === REASON_TYPES.BLACKOUT || reason?.type === REASON_TYPES.CREW_OFF
